import { useCallback, useState } from "react";

import { useNotification } from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { useTinaPoll } from "@/pages/tina/hooks/use-tina-poll";
import { useSitesRepository } from "@/pages/tina/sites/hooks/use-sites-repository";
import type { DiscoverTestRead } from "@/types/tina";

const isTerminal = (data: DiscoverTestRead | undefined) =>
  data?.status === "done" || data?.status === "failed";

export function useDiscoverTest(siteName: string) {
  const repo = useSitesRepository();
  const { open } = useNotification();
  const [testId, setTestId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const start = useCallback(async () => {
    setStarting(true);
    setTestId(null);
    try {
      const res = await repo.startDiscoverTest(siteName);
      setTestId(res.id);
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to start discover test",
        description: toHumanErrorMessage(error),
      });
    } finally {
      setStarting(false);
    }
  }, [repo, siteName, open]);

  const reset = useCallback(() => setTestId(null), []);

  const query = useTinaPoll<DiscoverTestRead | undefined>(
    ["discover-test", siteName, testId],
    () => repo.getDiscoverTest(siteName, testId as string),
    {
      enabled: Boolean(testId),
      shouldPoll: (data) => !isTerminal(data),
    },
  );

  return {
    start,
    reset,
    starting,
    testId,
    data: query.data,
    isLoading: Boolean(testId) && !isTerminal(query.data),
  };
}
